// Překlady a formáty. Stejné klíče a stejná pravidla množného čísla jako na serveru (Texts.php).

type TextValue = string | Record<string, string>;

let texts: Record<string, TextValue> = {};
let lang = 'cs';

export function setTexts(t: Record<string, TextValue>, l: string): void {
  texts = t;
  lang = l;
  document.documentElement.lang = l;
}

export function currentLang(): string {
  return lang;
}

export function pluralForm(n: number): string {
  if (lang === 'cs' || lang === 'sk') return n === 1 ? 'one' : n >= 2 && n <= 4 ? 'few' : 'other';
  return n === 1 ? 'one' : 'other';
}

export function t(key: string, params?: Record<string, unknown>): string {
  const v = texts[key];
  if (v === undefined) return key;
  let s: string;
  if (typeof v === 'string') s = v;
  else {
    const n = Number(params?.['count'] ?? 0);
    s = v[pluralForm(n)] ?? v['other'] ?? key;
  }
  if (params) for (const [k, val] of Object.entries(params)) s = s.split('{' + k + '}').join(String(val ?? ''));
  return s;
}

/** Přeloží klíč, pokud vypadá jako klíč (obsahuje tečku a žádnou mezeru); jinak vrátí text beze změny. */
export function tk(keyOrText: string, params?: Record<string, unknown>): string {
  if (/^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/i.test(keyOrText)) return t(keyOrText, params);
  return keyOrText;
}

export function has(key: string): boolean {
  return texts[key] !== undefined;
}

// ---- formáty (Intl podle jazyka)

const locale = () => (lang === 'cs' ? 'cs-CZ' : lang === 'sk' ? 'sk-SK' : 'en-GB');

/** Čas z DB je UTC "YYYY-MM-DD HH:MM:SS" nebo ISO; převede na Date. */
export function parseDate(v: unknown): Date | null {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return v;
  if (typeof v !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(v);
  if (!m) return null;
  if (m[4] === undefined) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(v)) return new Date(v);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] ?? '0')));
}

export function fmtDate(v: unknown): string {
  const d = parseDate(v);
  return d ? new Intl.DateTimeFormat(locale(), { dateStyle: 'medium' }).format(d) : '';
}

export function fmtDateTime(v: unknown): string {
  const d = parseDate(v);
  return d ? new Intl.DateTimeFormat(locale(), { dateStyle: 'medium', timeStyle: 'short' }).format(d) : '';
}

export function fmtTime(d: Date): string {
  return new Intl.DateTimeFormat(locale(), { timeStyle: 'short' }).format(d);
}

export function fmtNumber(v: unknown, digits?: number): string {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat(locale(), digits === undefined ? {} : { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
}

export function fmtSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  const units = ['kB', 'MB', 'GB', 'TB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return fmtNumber(v, v < 10 ? 1 : 0) + ' ' + units[i];
}

export function fmtRelative(v: unknown): string {
  const d = parseDate(v);
  if (!d) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: 'auto' });
  if (diff < 60) return rtf.format(-Math.round(diff), 'second');
  if (diff < 3600) return rtf.format(-Math.round(diff / 60), 'minute');
  if (diff < 86400) return rtf.format(-Math.round(diff / 3600), 'hour');
  if (diff < 86400 * 30) return rtf.format(-Math.round(diff / 86400), 'day');
  return fmtDate(d);
}

/** Datum pro <input type=date> (lokální). */
export function toInputDate(v: unknown): string {
  const d = parseDate(v);
  if (!d) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function toInputDateTime(v: unknown): string {
  const d = parseDate(v);
  if (!d) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
